import React from "react";
import { Trans as DS_Trans, useTranslation as DS_useTranslation } from "@unicity/design-system";
import { Trans as RI_Trans, useTranslation as RI_useTranslation } from "react-i18next";
import { toTitleCase, toUpperCase } from "../common/format-functions";
import { TranslateProps, TranslateMonthProps } from "../types/components/language";
// Fallback to react-i18next if design-system doesn't export these yet
const useTranslation = DS_useTranslation ?? RI_useTranslation;
const Trans = DS_Trans ?? RI_Trans;

/**
 * @description This component is used to translate a string with parameters.
 * @param {TranslateProps} props - The props for the T component.
 * @param {string} props.children - The key of the string to translate.
 * @param {boolean} props.isUpperCase - Whether to convert the string to uppercase.
 * @param {boolean} props.isTitleCase - Whether to convert the string to title case.
 * @param {React.ReactNode[]} props.components - The components to use in the translated string.
 * @param {any} props.params - The parameters to use in the translated string.
 */
const T = ({ children, isUpperCase: _isUpperCase, isTitleCase: _isTitleCase, components, ...params }: TranslateProps): React.ReactNode => {
  const { ready } = useTranslation();

  if (!ready) return null;

  return (
    <Trans
      i18nKey={children}
      values={params}
      defaults={children}
      components={components}
      tOptions={{ ...params }}
    >
      {children}
    </Trans>
  );
};

/**
 * @description This component is used to translate a month.
 * @param {TranslateMonthProps} props - The props for the TM component.
 * @param {string} props.children - The month to translate.
 * @returns {React.ReactNode} The translated month.
 */
const TM = ({ children }: TranslateMonthProps): React.ReactNode => {
  const { t, ready } = useTranslation();

  if (!ready) return null;

  const dateSplit = children.split(" ");
  const translatedMonth = t(dateSplit[0].toLowerCase(), { defaultValue: dateSplit[0] }) as string;

  return <>{`${translatedMonth} ${dateSplit.length > 1 ? dateSplit[1] : ""}`}</>;
};

/**
 * @description This component is used to translate a string with parameters.
 * @param {TranslateProps} props - The props for the TElement component.
 * @param {string} props.children - The key of the string to translate.
 * @param {boolean} props.isUpperCase - Whether to convert the string to uppercase.
 * @param {boolean} props.isTitleCase - Whether to convert the string to title case.
 * @param {any} props.params - The parameters to use in the translated string.
 */
const TElement = ({ children, isUpperCase, isTitleCase, ...params }: TranslateProps): React.ReactNode => {
  const { t, ready } = useTranslation();

  if (!ready) return null;

  let translatedString = t(children, { ...params, defaultValue: children }) as string;

  if (isUpperCase && translatedString) translatedString = toUpperCase(translatedString);
  if (isTitleCase && translatedString) translatedString = toTitleCase(translatedString);

  if (!translatedString) return <>{`{{${children}}}`}</>;

  const parts = translatedString.split(/\{(\d+)\}/);

  return (
    <>
      {parts.map((part, index) => {
        if (index % 2 === 0) return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
        const paramValue = params[`param${part}`] || `{${part}}`;
        return <React.Fragment key={`param-${index}`}>{paramValue}</React.Fragment>;
      })}
    </>
  );
};

/**
 * @description This hook is used to translate a string with parameters.
 * @param {string} key - The key of the string to translate.
 * @param {any} params - The parameters to use in the translated string.
 * @returns {string} The translated string.
 */
const useTranslateKey = (key: string, ...params: unknown[]): string => {
  const { t, ready } = useTranslation();
  if (!ready) return key;
  return t(key, { ...params, defaultValue: key }) as string;
};

export { T, TM, TElement, useTranslateKey, useTranslation };